import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { statusLight } from '../../theme';

// One small burst, once, when somebody is finally cleared to drive.
//
// `fa_compliant` is the moment a candidate stops being a pile of
// outstanding paperwork and becomes a driver who is allowed in a van — the
// only point in the whole run that is genuinely worth marking. Everything
// else on these screens is deliberately quiet, so this stays short
// (900ms), small, and behind the content.
//
// Canvas rather than a stack of animated divs: forty particles as DOM
// nodes means forty elements being laid out every frame, on a page that is
// already doing real work.

const PARTICLES = 46;
const DURATION = 900;

/** Brand-ish, plus the ok green. Nothing outside the palette. */
const COLORS = [statusLight.ok, statusLight.info, '#0077FF', '#7FB9FF', statusLight.warn];

interface Particle {
  x: number; y: number; vx: number; vy: number; size: number; color: string; spin: number;
}

const Confetti: React.FC<{
  /** Viewport coordinates to burst from — where the tick actually was. */
  origin?: { x: number; y: number } | null;
  onDone?: () => void;
}> = ({ origin, onDone }) => {
  const canvas = useRef<HTMLCanvasElement>(null);
  const finished = useRef(onDone);
  finished.current = onDone;

  useEffect(() => {
    // Somebody who has asked for less motion has asked for less motion.
    // The tick going green already tells them it worked.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (still) {
      const t = window.setTimeout(() => finished.current?.(), 400);
      return () => window.clearTimeout(t);
    }

    const el = canvas.current;
    if (!el) return undefined;
    const ctx = el.getContext('2d');
    if (!ctx) return undefined;

    const dpr = window.devicePixelRatio || 1;
    const w = el.clientWidth;
    const h = el.clientHeight;
    el.width = w * dpr;
    el.height = h * dpr;
    ctx.scale(dpr, dpr);

    // From where the tick was clicked. A burst in the middle of the
    // screen is a notification about something; a burst at the control is
    // the control responding.
    const originX = origin ? origin.x : w / 2;
    const originY = origin ? origin.y : h * 0.42;
    const particles: Particle[] = Array.from({ length: PARTICLES }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 2.4 + Math.random() * 4.2;
      return {
        x: originX,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1.6,
        size: 3 + Math.random() * 3.5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        spin: Math.random() * Math.PI,
      };
    });

    const started = performance.now();
    let frame = 0;

    const draw = (now: number) => {
      const elapsed = now - started;
      const progress = Math.min(1, elapsed / DURATION);
      ctx.clearRect(0, 0, w, h);

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.16;        // gravity
        p.vx *= 0.99;        // a little drag, so it settles rather than flies
        p.spin += 0.2;

        ctx.save();
        ctx.globalAlpha = 1 - progress * progress;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.spin);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      });

      if (progress < 1) {
        frame = requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, w, h);
        finished.current?.();
      }
    };

    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [origin]);

  return (
    <Box
      aria-hidden
      data-testid="celebration"
      sx={{
        position: 'fixed',
        inset: 0,
        // Behind nothing it needs to block, and above nothing it could
        // stop somebody clicking.
        pointerEvents: 'none',
        zIndex: (theme) => theme.zIndex.snackbar - 1,
      }}
    >
      <canvas ref={canvas} style={{ width: '100%', height: '100%' }} />
    </Box>
  );
};

export default Confetti;
